// src/pages/Pursuit/ManagerCreatedAssessmentsPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Heading,
    Text,
    Button,
    TextInput,
    Layer,
    Spinner,
    DropButton,
    CheckBoxGroup,
    Notification,
} from "grommet";
import { Search, FormDown } from "grommet-icons";

import axios from "axios";

import { HeaderNavigation } from "../../components/Headers/PursuitHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import BreadcrumbNavigation from "../../components/AdminPortal/BreadcrumbNavigation";
import ManagerAssessmentsTable, { getOrganisationFromEmail } from "../../components/PursuitAccess/ManagerAssessmentsTable";
import countryRegion from "../../components/PursuitAccess/country_region.json";
import ExportManagerAssessmentsButton from "../../components/PursuitAccess/ExportManagerAssessmentsButton";

const dateOptions = [
    { label: "1 Week", value: "1w", days: 7 },
    { label: "1 Month", value: "1m", days: 30 },
    { label: "3 Months", value: "3m", days: 90 },
    { label: "Max (6 months)", value: "all", days: null },
];

const regionOptions = Object.keys(countryRegion).map((region) => ({
    label: region,
    value: region,
}));

const ManagerCreatedAssessmentsPage = () => {
    const [assessments, setAssessments] = useState([]); // <-- grouped shape: [{assessment_id, assessment_meta, entries: []}]
    const [loading, setLoading] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [dateRange, setDateRange] = useState("all");
    const [regions, setRegions] = useState([]);
    const [orgFilter, setOrgFilter] = useState([]);
    const [error, setError] = useState(null);
    const [toastMessage, setToastMessage] = useState(null);

    // Build country set for region filter
    const selectedCountries = useMemo(() => {
        if (!Array.isArray(regions) || regions.length === 0) return null;
        const all = regions.reduce((acc, reg) => acc.concat(countryRegion[reg] || []), []);
        return new Set(all.map((c) => c.toLowerCase()));
    }, [regions]);

    useEffect(() => {
        const fetchAssessments = async () => {
            setLoading(true);
            try {
                const res = await axios.get("/api/pursuit/get-manager-assessments");
                // Expect: { assessments: [ { assessment_id, assessment_meta, entries: [...] } ] }
                setAssessments(Array.isArray(res.data.assessments) ? res.data.assessments : []);
            } catch (err) {
                setError("Error fetching manager assessments");
            } finally {
                setLoading(false);
            }
        };
        fetchAssessments();
    }, []);

    const orgOptions = useMemo(() => {
        const unique = new Set((assessments || []).map(g => getOrganisationFromEmail(g.entries?.[0]?.manager_email || "")).filter(Boolean));
        return Array.from(unique).sort((a, b) => a.localeCompare(b));
    }, [assessments]);

    const lower = searchValue.toLowerCase().trim();

    const filteredAssessments = useMemo(() => {
        const inDateRange = (creationISO) => {
            if (dateRange === "all") return true;
            const spec = dateOptions.find((o) => o.value === dateRange);
            if (!spec?.days) return true;
            const created = creationISO ? new Date(creationISO).getTime() : 0;
            const since = Date.now() - spec.days * 24 * 3600 * 1000;
            return created >= since;
        };

        const matchRegion = (mgrCountry) => {
            if (!selectedCountries) return true;
            return selectedCountries.has(String(mgrCountry || "").toLowerCase());
        };

        const matchesSearch = (group) => {
            if (!lower) return true;

            const meta = group.assessment_meta || {};
            const name = String(meta.assessment_name || "").toLowerCase();
            const id = String(group.assessment_id || "").toLowerCase();

            const first = (group.entries && group.entries[0]) || {};
            const managerName = String(first.manager_name || "").toLowerCase();
            const managerEmail = String(first.manager_email || "").toLowerCase();
            const managerCountry = String(first.manager_country || "").toLowerCase();

            const topics = (group.entries || [])
                .flatMap((e) => [e.topic1_name, e.topic2_name, e.topic3_name, e.topic4_name, e.topic5_name])
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return (
                name.includes(lower) ||
                id.includes(lower) ||
                managerName.includes(lower) ||
                managerEmail.includes(lower) ||
                managerCountry.includes(lower) ||
                topics.includes(lower)
            );
        };

        return (assessments || []).filter((g) => {
            const meta = g.assessment_meta || {};
            const createdOk = inDateRange(meta.creation_date);
            const first = (g.entries && g.entries[0]) || {};
            const regionOk = matchRegion(first.manager_country);
            const searchOk = matchesSearch(g);
            return createdOk && regionOk && searchOk;
        });
    }, [assessments, dateRange, selectedCountries, lower]);

    return (
        <Box fill="vertical" background="light-2">
            <HeaderNavigation />
            <BreadcrumbNavigation
                breadcrumbs={[
                    { label: "Dashboard", link: `/pursuit/dashboard` },
                    { label: "Manager Created Assessments", link: `/pursuit/manager-assessments` },
                ]}
            />
            <Box height="2px" background="brand" margin={{ vertical: "small" }} />

            <Box flex pad={{ horizontal: "medium", vertical: "xsmall" }}>
                {/* Header */}
                <Box direction="row" align="center" justify="between">
                    <Heading level="2" margin="none" color="brand">
                        Manager Created Assessments
                    </Heading>
                    <Box direction="row" gap="small" align="center">
                        <ExportManagerAssessmentsButton
                            groups={filteredAssessments}
                            onDone={(msg) => setToastMessage(msg)}
                        />
                        <Button
                            label="Go to User Self Assessments"
                            secondary
                            onClick={() => {
                                window.location.href = "/pursuit/self-assessments";
                            }}
                        />
                    </Box>
                </Box>
                <Text margin={{ bottom: "xsmall" }}>
                    This page displays assessments created by managers. Use the filters below to narrow your view.
                </Text>

                {/* Filters */}
                <Box
                    flex={false}
                    direction="row"
                    align="center"
                    gap="medium"
                    margin={{ top: "none", bottom: "small" }}
                >
                    {/* Search */}
                    <Box flex={false}>
                        <TextInput
                            icon={<Search />}
                            placeholder="Search by assessment name, manager, topic, or country"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            width="500px"
                        />
                    </Box>

                    {/* Date Range (based on assessment_meta.creation_date) */}
                    <DropButton
                        label={dateOptions.find((o) => o.value === dateRange)?.label || "All Time"}
                        icon={<FormDown />}
                        dropAlign={{ top: "bottom", left: "left" }}
                        dropContent={
                            <Box pad="small" gap="small">
                                {dateOptions.map((opt) => (
                                    <Button
                                        key={opt.value}
                                        plain
                                        hoverIndicator
                                        onClick={() => setDateRange(opt.value)}
                                    >
                                        <Box pad="xsmall">
                                            <Text color={dateRange === opt.value ? "brand" : undefined}>
                                                {opt.label}
                                            </Text>
                                        </Box>
                                    </Button>
                                ))}
                            </Box>
                        }
                    />

                    {/* Region (manager_country) */}
                    <DropButton
                        label="Region"
                        icon={<FormDown />}
                        dropAlign={{ top: "bottom", left: "left" }}
                        dropContent={
                            <Box pad="small">
                                <CheckBoxGroup
                                    options={regionOptions}
                                    value={regions}
                                    onChange={({ value }) => setRegions(value)}
                                />
                            </Box>
                        }
                    />

                    {/* Organisation */}
                    <DropButton
                        label={orgFilter.length ? `Organisation: ${orgFilter.join(", ")}` : "Filter by Organisation"}
                        icon={<FormDown />}
                        dropAlign={{ top: "bottom", left: "left" }}
                        dropContent={
                            <Box pad="medium" width="medium">
                                <Text weight="bold" margin={{ bottom: "xsmall" }}>Organisation filter</Text>
                                <CheckBoxGroup
                                    options={orgOptions}
                                    value={orgFilter}
                                    onChange={({ value }) => setOrgFilter(value)}
                                />
                                <Box margin={{ top: "small" }}>
                                    <Button label="Clear" onClick={() => setOrgFilter([])} secondary size="small" />
                                </Box>
                            </Box>
                        }
                    />
                </Box>

                {/* Table */}
                <Box flex overflow={{ horizontal: "auto" }}>
                    {loading ? (
                        <Box fill align="center" justify="center">
                            <Spinner size="large" />
                            <Text margin={{ top: "small" }}>Loading…</Text>
                        </Box>
                    ) : error ? (
                        <Box fill align="center" justify="center">
                            <Text color="status-critical">{error}</Text>
                        </Box>
                    ) : (
                        <ManagerAssessmentsTable
                            key={Array.isArray(regions) ? regions.join(",") : "no-regions"}
                            assessmentData={filteredAssessments}
                            orgFilter={orgFilter}
                        />
                    )}
                </Box>
            </Box>

            <StickyFooter />

            {toastMessage && (
                <Notification
                    toast
                    status="normal"
                    title="Success"
                    message={toastMessage}
                    onClose={() => setToastMessage(null)}
                />
            )}
        </Box>
    );
};

export default ManagerCreatedAssessmentsPage;
